// Angular Native Modules
import { ErrorHandler, Injector, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';

// Hybrid Support - TODO: Remove these modules once hybrid support is removed
import { downgradeComponent, downgradeInjectable, UpgradeModule } from '@angular/upgrade/static';
import { locationProvider, logoutServiceProvider, rootScopeProvider, routeProvider, templateValueCalculatorProvider } from 'app/ajs-upgraded-providers';
import { Provisioning } from "ng/src/app/log-settings/provisioning/provisioning.component";
import { CommandContainerComponent } from 'ng/src/app/shared/command-palette/command-container/command-container.component';
import { CommandPaletteService } from 'ng/src/app/shared/command-palette/command-palette.service';
import { LoadingIndicatorComponent } from "ng/src/app/shared/loading-indicator/loading-indicator.component";
import { SingleShiftSignOnComponent } from "ng/src/app/shifts/single-shift-sign-on/single-shift-sign-on.component";
import { FormInputDialogComponent } from "ng/src/app/shared/form-input-dialog/form-input-dialog.component";
import { FormInputDialogService } from "ng/src/app/shared/form-input-dialog/form-input-dialog.service";
import { PToastWrapper } from './hybrid-helpers/p-toast-wrapper.component';
import { PConfirmDialogWrapper } from './hybrid-helpers/p-confirm-dialog-wrapper.component';

// PrimeNG
import { ToastModule } from 'primeng/toast';
import { ConfirmationService, MessageService } from 'primeng/api';
import { TableModule } from 'primeng/table';
import { Button, ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { Divider } from 'primeng/divider';
import { Dialog } from 'primeng/dialog';
import { Accordion, AccordionTab } from 'primeng/accordion';

// Feature Modules
import { CoreModule } from './core/core.module';
import { SharedModule } from './shared/shared.module';
import { NoteModule } from './notes/note.module';
import { LogModule } from './logs/log.module';
import { NotificationModule } from './notifications/notification.module';
import { AlertModule } from 'ng/src/app/alerts/alert.module';
import { ShiftModule } from 'ng/src/app/shifts/shift.module';
import { ReportModule } from './reports/report.module';
import { UsersModule } from "ng/src/app/users/users.module";
import { ChoiceListModule } from "ng/src/app/choice-lists/choice-list.module";
import { TagsModule } from "ng/src/app/tags/tags.module";

// Services
import { ErrorService } from './core/services/error.service';
import { LogBookService } from './core/services/logbook.service';
import { ReportService } from './reports/report.service';
import { TagService } from './tags/tag.service';
import { ImageViewerService } from './core/image-viewer/image-viewer.service';
import { ReminderService } from './reminders/reminders.service';
import { NotificationService } from './notifications/notification.service';
import { AlertService } from 'ng/src/app/alerts/shared/alert.service';
import { ShiftsService } from 'ng/src/app/shifts/shifts.service';
import { HttpInterceptorClientService } from './core/services/http-interceptor-client.service';
import { AuthClientService } from 'ng/src/app/authentication/auth-client.service';
import { PrimeNGHelpersService } from './core/services/primeng-helpers.service';
import { NoteService } from './notes/shared/note.service';
import { LogService } from './logs/log.service';
import { AppStateService } from './core/services/app-state.service';
import { UserService } from 'ng/src/app/users/shared/user.service';

import { CookieService } from 'ngx-cookie-service';

// Components
import { LogListComponent } from './logs/log-list/log-list.component';
import { ImageViewerComponent } from './core/image-viewer/image-viewer.component';
import { NotificationPageComponent } from './notifications/notification-page/notifications-page.component';
import { NotificationListComponent } from './notifications/notification-list/notification-list.component';
import { NotificationEntryComponent } from './notifications/notification-entry/notification-entry.component';
import { ReminderTickerComponent } from './reminders/reminder-ticker.component';
import { NotesTableComponent } from 'ng/src/app/notes/notes-table/notes-table.component';
import { DraftNotesComponent } from './notes/draft-notes/draft-notes.component';
import { AlertInstanceTableComponent } from 'ng/src/app/alerts/instances/alert-instance-table.component';
import { ServiceWorkerService } from 'service-worker/service-worker.service';
import { SignoffControlsComponent } from 'ng/src/app/notes/signoffs/signoff-controls.component';
import { ShiftIndicatorComponent } from 'ng/src/app/shifts/indicator/shift-indicator.component';
import { NavbarFabComponent } from './core/navbar/navbar-fab.component';
import { NoteEntryTocComponent } from "./notes/note-entry-toc/note-entry-toc.component";
import { TextEditorComponent } from './shared/text-editor/text-editor.component';
import { CommentListComponent } from './shared/comment-list/comment-list.component';
import { NoteExportService } from './notes/shared/note-export.service';
import { ExportDialogComponent } from './shared/pdf-export/export-dialog.component';
import { NoteDetailComponent } from './notes/note-detail/note-detail.component';
import { FilesComponent } from "ng/src/app/files/files/files.component";
import { PreviewViewerComponent } from "ng/src/app/core/preview-viewer/preview-viewer.component";
import { SimulateLogUserRoleService } from "ng/src/app/shared/simulate-log-user-role.service";
import { PrivilegeIndicatorComponent } from './shared/privilege-indicator/privilege-indicator.component';
import { TimezoneSelectorComponent } from "ng/src/app/log-settings/timezone-selector/timezone-selector.component";
import { LogSettingsModule } from "ng/src/app/log-settings/log-settings.module";
import { PastValueViewerComponent } from "ng/src/app/notes/past-values/past-value-viewer/past-value-viewer.component";
import { MathJsService } from 'ng/src/app/notes/shared/mathjs.service';
import { PersistentDialogComponent } from "ng/src/app/shared/persistent-dialog/persistent-dialog.component";
import { PersistentDialogService } from "ng/src/app/shared/persistent-dialog/persistent-dialog.service";
import { EnterNotesComponent } from "ng/src/app/notes/enter-notes/enter-notes.component";
import { TemplateService } from "ng/src/app/templates/shared/template.service";
import { NoteControllerComponent } from "ng/src/app/notes/note-controller/note-controller.component";
import { EnterNoteSelectorComponent } from "ng/src/app/log-settings/enter-note-selector/enter-note-selector.component";
import { CommonModule } from "@angular/common";
import { TemplateModule } from './templates/template.module';
import { TemplateCopierComponent } from "ng/src/app/templates/template-copier/template-copier.component";
import { ChoicelistCopierComponent } from "ng/src/app/choice-lists/choicelist-copier.component";
import { TemplateSegmentClipboardService } from "ng/src/app/templates/clipboard/template-segment-clipboard.service";
import { TemplateSegmentClipboardComponent } from "ng/src/app/templates/clipboard/template-segment-clipboard.component";
import { HelpDialogComponent } from "ng/src/app/core/help-dialog/help-dialog.component";
import { HelpDialogService } from "ng/src/app/core/help-dialog/help-dialog.service";
import { NoteRefreshComponent } from "ng/src/app/templates/note-refresh/note-refresh.component";
import { DualNotificationContainerComponent } from "ng/src/app/notifications/dual-notification-container/dual-notification-container.component";
import { NotificationButtonComponent } from "ng/src/app/notifications/notification-button/notification-button.component";
import { SystemNotificationNotifierService } from "ng/src/app/notifications/system-notification-notifier/system-notification-notifier.service";
import { SystemNotificationService } from "ng/src/app/notifications/system-notification.service";
import { TemplateListComponent } from "ng/src/app/templates/list/template-list.component";

@NgModule({
	declarations: [
		PToastWrapper,
		PConfirmDialogWrapper,
		PrivilegeIndicatorComponent
	],
	imports: [
		CommonModule,
		UpgradeModule,
		BrowserModule,
		BrowserAnimationsModule,
		HttpClientModule,
		ToastModule,
		TableModule,
		ButtonModule,
		ConfirmDialogModule,
		CoreModule,
		SharedModule,
		NoteModule,
		LogModule,
		AlertModule,
		NotificationModule,
		ShiftModule,
		LogSettingsModule,
		ReportModule,
		UsersModule,
		ChoiceListModule,
		TagsModule,
		TemplateModule
	],
	providers: [
		locationProvider,
		rootScopeProvider,
		routeProvider,
		logoutServiceProvider,
		templateValueCalculatorProvider,
		CookieService,
		MessageService,
		ConfirmationService,
		ServiceWorkerService,
		{ provide: HTTP_INTERCEPTORS, useClass: HttpInterceptorClientService, multi: true },
		{ provide: ErrorHandler, useClass: ErrorService }
	]
})
export class AppModule {
	constructor(
		private upgrade: UpgradeModule,
		private systemNotificationNotifierService: SystemNotificationNotifierService
	) { }

	ngDoBootstrap() {
		// Make Angular services and components available to AngularJS
		angular.module('logbook')
			.factory('injector', downgradeInjectable(Injector))
			.factory('alertService', downgradeInjectable(AlertService))
			.factory('noteService', downgradeInjectable(NoteService))
			.factory('appStateService', downgradeInjectable(AppStateService))
			.factory('simulateLogUserRoleService', downgradeInjectable(SimulateLogUserRoleService))
			.factory('errorService', downgradeInjectable(ErrorService))
			.factory('logService', downgradeInjectable(LogService))
			.factory('userService', downgradeInjectable(UserService))
			.factory('cookieService', downgradeInjectable(CookieService))
			.factory('logBookService', downgradeInjectable(LogBookService))
			.factory('reportService', downgradeInjectable(ReportService))
			.factory('tagService', downgradeInjectable(TagService))
			.factory('notificationService', downgradeInjectable(NotificationService))
			.factory('systemNotificationService', downgradeInjectable(SystemNotificationService))
			.factory('reminderService', downgradeInjectable(ReminderService))
			.factory('imageViewerService', downgradeInjectable(ImageViewerService))
			.factory('serviceWorkerService', downgradeInjectable(ServiceWorkerService))
			.factory('primeNGHelpersService', downgradeInjectable(PrimeNGHelpersService))
			.factory('pConfirmationService', downgradeInjectable(ConfirmationService))
			.factory('shiftsService', downgradeInjectable(ShiftsService))
			.factory('authService', downgradeInjectable(AuthClientService))
			.factory('noteExportService', downgradeInjectable(NoteExportService))
			.factory('mathJsService', downgradeInjectable(MathJsService))
			.factory('persistentDialogService', downgradeInjectable(PersistentDialogService))
			.factory('templateService', downgradeInjectable(TemplateService))
			.factory('userService', downgradeInjectable(UserService))
			.factory('templateSegmentClipboardService', downgradeInjectable(TemplateSegmentClipboardService))
			.factory('helpDialogService', downgradeInjectable(HelpDialogService))
			.factory('formInputDialogService', downgradeInjectable(FormInputDialogService))
			.factory('commandPaletteService', downgradeInjectable(CommandPaletteService))
			.directive('reminderTicker', downgradeComponent({
				component: ReminderTickerComponent,
				propagateDigest: false
			}))
			.directive('pButtonAjs', downgradeComponent({
				component: Button,
				propagateDigest: false
			}))
			.directive('pDividerAjs', downgradeComponent({
				component: Divider,
				propagateDigest: false
			}))
			.directive('pToastAjs', downgradeComponent({
				component: PToastWrapper,
				propagateDigest: false
			}))
			.directive('pConfirmDialogAjs', downgradeComponent({
				component: PConfirmDialogWrapper,
				propagateDigest: false
			}))
			.directive('logList', downgradeComponent({
				component: LogListComponent,
				propagateDigest: false
			}))
			.directive('notesTable', downgradeComponent({
				component: NotesTableComponent,
				propagateDigest: false
			}))
			.directive('lbImageViewerAjs', downgradeComponent({
				component: ImageViewerComponent,
				propagateDigest: false
			}))
			.directive('lbNavbarFabAjs', downgradeComponent({
				component: NavbarFabComponent,
				propagateDigest: false
			}))
			.directive('notificationPage', downgradeComponent({
				component: NotificationPageComponent,
				propagateDigest: false
			}))
			.directive('notificationList', downgradeComponent({
				component: NotificationListComponent,
				propagateDigest: false
			}))
			.directive('notificationEntry', downgradeComponent({
				component: NotificationEntryComponent,
				propagateDigest: false
			}))
			.directive('pDialogAjs', downgradeComponent({
				component: Dialog,
				propagateDigest: false
			}))
			.directive('pAccordionAjs', downgradeComponent({
				component: Accordion,
				propagateDigest: false
			}))
			.directive('pAccordionTabAjs', downgradeComponent({
				component: AccordionTab,
				propagateDigest: false
			}))
			.directive('lbTextEditorAjs', downgradeComponent({
                component: TextEditorComponent,
                propagateDigest: false
            }))
			.directive('lbDraftNotesAjs', downgradeComponent({
				component: DraftNotesComponent,
				propagateDigest: false
			}))
			.directive('alertInstances', downgradeComponent({
				component: AlertInstanceTableComponent,
				propagateDigest: false
			}))
			.directive('signoffControls', downgradeComponent({
				component: SignoffControlsComponent,
				propagateDigest: false
			}))
			.directive('shiftIndicator', downgradeComponent({
				component: ShiftIndicatorComponent,
				propagateDigest: false
			}))
			.directive('privilegeIndicator', downgradeComponent({
				component: PrivilegeIndicatorComponent,
				propagateDigest: false
			}))
			.directive('lbNoteEntryToc', downgradeComponent({
				component: NoteEntryTocComponent,
				propagateDigest: false
			}))
			.directive('lbCommentListAjs', downgradeComponent({
                component: CommentListComponent,
                propagateDigest: false
			}))
			.directive('lbExportDialogAjs', downgradeComponent({
                component: ExportDialogComponent,
                propagateDigest: false
			}))
			.directive('noteDetail', downgradeComponent({
				component: NoteDetailComponent,
				propagateDigest: false
			}))
			.directive('lbFiles', downgradeComponent({
				component: FilesComponent,
				propagateDigest: false
			}))
			.directive('lbPreviewViewer', downgradeComponent({
				component: PreviewViewerComponent,
				propagateDigest: false
			}))
			.directive('lbPastValueViewer', downgradeComponent({
				component: PastValueViewerComponent,
				propagateDigest: false
			}))
			.directive('lbTimezoneSelector', downgradeComponent({
				component: TimezoneSelectorComponent,
				propagateDigest: false
			}))
			.directive('templateCopierAjs', downgradeComponent({
				component: TemplateCopierComponent,
				propagateDigest: false
			}))
			.directive('lbChoiceListCopierAjs', downgradeComponent({
				component: ChoicelistCopierComponent,
				propagateDigest: false
			}))
			.directive('lbDialogAjs', downgradeComponent({
				component: PersistentDialogComponent,
				propagateDigest: false
			}))
			.directive('lbEnterNotes', downgradeComponent({
				component: EnterNotesComponent,
				propagateDigest: false
			}))
			.directive('lbNoteControllerAjs', downgradeComponent({
				component: NoteControllerComponent,
				propagateDigest: false
			}))
			.directive('lbEnterNoteSelector', downgradeComponent({
				component: EnterNoteSelectorComponent,
				propagateDigest: false
			}))
			.directive('lbTemplateSegmentClipboard', downgradeComponent({
				component: TemplateSegmentClipboardComponent,
				propagateDigest: false
			}))
			.directive('lbHelpDialog', downgradeComponent({
				component: HelpDialogComponent,
				propagateDigest: false
			}))
			.directive('lbNoteRefresh', downgradeComponent({
				component: NoteRefreshComponent,
				propagateDigest: false
			}))
			.directive('lbTemplateList', downgradeComponent({
				component: TemplateListComponent,
				propagateDigest: false
			}))
			.directive('lbFormInputAjs', downgradeComponent({
				component: FormInputDialogComponent,
				propagateDigest: false
			}))
			.directive('lbNotificationListContainerAjs', downgradeComponent({
				component: DualNotificationContainerComponent,
				propagateDigest: false
			}))
			.directive('lbNotificationButtonAjs', downgradeComponent({
				component: NotificationButtonComponent,
				propagateDigest: false
			}))
			.directive('lbSingleShiftSignOn', downgradeComponent({
				component: SingleShiftSignOnComponent,
				propagateDigest: false
			}))
			.directive('lbLoadingIndicatorAjs', downgradeComponent({
				component: LoadingIndicatorComponent,
				propagateDigest: false
			}))
			.directive('lbProvisionAjs', downgradeComponent({
				component: Provisioning,
				propagateDigest: false
			}))
			.directive('lbCommandContainerAjs', downgradeComponent({
				component: CommandContainerComponent,
				propagateDigest: false
			}))
		;
		
		this.upgrade.bootstrap(document.documentElement, ['logbook'], { strictDi: true });
	}
}
